import { View, Platform, Pressable, KeyboardAvoidingView, ScrollView, Share, TextInput } from 'react-native';
import DefaultScreen from '../../../components/Global/DefaultScreenView';
import { Text, Title } from '@constants/Themed';
import { Image } from 'expo-image';
import styles from '@stylesheet';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import apis from '@api/api';
import moment from 'moment';
import { AntDesign, FontAwesome, Foundation, MaterialIcons, Ionicons } from '@expo/vector-icons';
import TimeLineScroll from './Components/TimeLineScroll';
import HistoryComponent from '@components/Global/HistoryComponent';
import MapView, { Circle, Marker } from 'react-native-maps';
import toTitleCase from '@hooks/toTitleCase';
import * as Linking from 'expo-linking';
import Button from '../../../components/UI/Button/Button';
import HorizontalCarousel from "../../../components/Global/Carousel/HorizontalCarousel.native"
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import CustomerDefault from '@assets/--native/CustomerDefault';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import LottieView from '@utils/lottieAnimation';
import { BottomSheetModal, BottomSheetModalProvider, BottomSheetView } from '@gorhom/bottom-sheet';
import { Stepper, Step, StepLabel } from '@mui/material';
import Markdown from 'react-native-markdown-display';
import PreviewModal, { PreviewModalHandler } from '../MidLevel/DataScreen/TemplatesScreen/components/PreviewModal';
import { HIDE_USER_CONTEXT_MODAL } from '@reduxLocal/actions/types';
import RangePicker from '@components/Global/DateRangePicker/RangePicker';
import { SizableText, XStack, YStack, Card, H2, H5, H4, Separator } from 'tamagui';
import getEmailCount from '@utils/getEmailCount';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import UserIcon from '@assets/icons/UserIcon';
import checkValidEmail from '@utils/checkValidEmail';
import handleDataItemDelete from '../MidLevel/DataScreen/CaptureScreen/utils/handleDataItemDelete';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { flagItem } from '@api/api';
import { Alert } from 'react-native';
import { getConversations, getConversationMessages } from '@api/whatsappApi';

// Enhanced CRM Components
const ContactStatusBadge = ({ status, theme }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': case 'hot': return '#10B981';
      case 'warm': return '#F59E0B';
      case 'cold': return '#6B7280';
      case 'inactive': return '#EF4444';
      default: return '#6B7280';
    }
  };

  return (
    <View style={{
      backgroundColor: getStatusColor(status),
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 12,
      alignSelf: 'flex-start'
    }}>
      <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
        {status || 'Unknown'}
      </Text>
    </View>
  );
};

  // Quick Action Card Component
  const QuickActionCard = ({ icon, label, onPress, disabled = false, count, theme }: { 
    icon: any, 
    label: any, 
    onPress: any, 
    disabled?: boolean, 
    count?: any, 
    theme: any 
  }) => (
  <Pressable
    onPress={onPress}
    disabled={disabled}
    style={{
      backgroundColor: theme ? '#fff' : '#374151',
      borderRadius: 12,
      padding: Platform.select({ default: 12, web: 16 }),
      alignItems: 'center',
      minWidth: Platform.select({ default: 60, web: 80 }),
      borderWidth: 1,
      borderColor: disabled ? '#D1D5DB' : 'rgb(90,192,234)',
      opacity: disabled ? 0.5 : 1,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3
    }}
  >
    {icon}
    <Text style={{
      color: theme ? '#1F2937' : '#F9FAFB',
        fontSize: Platform.select({ default: 10, web: 12 }),
      fontWeight: '500',
      marginTop: 4,
      textAlign: 'center'
    }}>
      {label}
    </Text>
    {count !== undefined && (
      <View style={{
        backgroundColor: 'rgb(90,192,234)',
        borderRadius: 10,
        minWidth: 20,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 4
      }}>
        <Text style={{ color: 'white', fontSize: 10, fontWeight: '700' }}>
          {count}
        </Text>
      </View>
    )}
  </Pressable>
);

const ContactMetrics = ({ data, theme, healthData }) => {
  // Debug logging
  console.log('📊 ContactMetrics received healthData:', healthData);

  const calculateDaysSinceLastContact = () => {
    if (!data?.lastContactDate) return 'Never';
    const days = moment().diff(moment(data.lastContactDate), 'days');
    return days === 0 ? 'Today' : `${days} days ago`;
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#10B981'; // Green
    if (score >= 60) return '#F59E0B'; // Yellow
    if (score >= 40) return '#EF4444'; // Red
    return '#6B7280'; // grey
  };

  const metrics = [
    { label: 'Last Contact', value: calculateDaysSinceLastContact(), icon: '📅' },
    { label: 'Total Interactions', value: data?.totalInteractions || 0, icon: '💬' },
    { label: 'Deal Value', value: data?.dealValue ? `$${data.dealValue}` : 'N/A', icon: '💰' },
    { label: 'Lead Score', value: data?.leadScore || 'N/A', icon: '⭐' },
    {
      label: 'Health Score',
      value: healthData?.overallScore ? `${healthData.overallScore}/100` : 'Not Analyzed',
      icon: '🏥',
      color: healthData?.overallScore ? getHealthScoreColor(healthData.overallScore) : undefined
    }
  ];

  console.log('📋 ContactMetrics metrics array:', metrics);

  return (
    <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$3" marginVertical="$2">
      <H4 marginBottom="$2">Contact Metrics</H4>
      <YStack gap="$2">
        {metrics.map((metric, index) => (
          <XStack key={index} justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" gap="$2">
              <Text style={{ fontSize: 16 }}>{metric.icon}</Text>
              <SizableText size="$3">{metric.label}</SizableText>
            </XStack>
            <SizableText
              size="$3"
              fontWeight="600"
              color={metric.color || (theme ? '#1F2937' : '#F9FAFB')}
            >
              {metric.value}
            </SizableText>
          </XStack>
        ))}
      </YStack>
    </Card>
  );
};

const NotesSection = ({ notes = [], onAddNote, theme }) => {
  const [newNote, setNewNote] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);

  return (
    <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$3" marginVertical="$2">
      <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
        <H4>Notes</H4>
        <Pressable
          onPress={() => setShowAddNote(!showAddNote)}
          style={{
            backgroundColor: 'rgb(90,192,234)',
            borderRadius: 20,
            padding: 8
          }}
        >
          <AntDesign name="plus" size={16} color="white" />
        </Pressable>
      </XStack>
      
      {showAddNote && (
        <YStack gap="$2" marginBottom="$3">
          <TextInput
            style={{
              borderWidth: 1,
              borderColor: '#D1D5DB',
              borderRadius: 8,
              padding: 12,
              minHeight: 80,
              textAlignVertical: 'top',
              backgroundColor: theme ? '#fff' : '#374151',
              color: theme ? '#000' : '#fff'
            }}
            placeholder="Add a note..."
            placeholderTextColor={theme ? '#9CA3AF' : '#6B7280'}
            multiline
            value={newNote}
            onChangeText={setNewNote}
          />
          <XStack gap="$2">
            <Pressable
              onPress={() => {
                if (newNote.trim()) {
                  onAddNote(newNote.trim());
                  setNewNote('');
                  setShowAddNote(false);
                }
              }}
              style={{
                backgroundColor: 'rgb(90,192,234)',
                borderRadius: 6,
                padding: 8,
                flex: 1
              }}
            >
              <Text style={{ color: 'white', textAlign: 'center', fontWeight: '600' }}>
                Save Note
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                setNewNote('');
                setShowAddNote(false);
              }}
              style={{
                backgroundColor: '#6B7280',
                borderRadius: 6,
                padding: 8,
                flex: 1
              }}
            >
              <Text style={{ color: 'white', textAlign: 'center', fontWeight: '600' }}>
                Cancel
              </Text>
            </Pressable>
          </XStack>
        </YStack>
      )}

      <ScrollView style={{ maxHeight: 200 }}>
        {notes.length === 0 ? (
          <Text style={{ 
            color: theme ? '#6B7280' : '#9CA3AF', 
            fontStyle: 'italic',
            textAlign: 'center',
            padding: 20
          }}>
            No notes yet. Add your first note above.
          </Text>
        ) : (
          notes.map((note, index) => (
            <View key={index} style={{
              backgroundColor: theme ? '#F9FAFB' : '#4B5563',
              borderRadius: 8,
              padding: 12,
              marginBottom: 8,
              borderLeftWidth: 3,
              borderLeftColor: 'rgb(90,192,234)'
            }}>
              <Text style={{ 
                color: theme ? '#1F2937' : '#F9FAFB',
                fontSize: 14,
                lineHeight: 20
              }}>
                {note.text}
              </Text>
              <Text style={{ 
                color: theme ? '#6B7280' : '#9CA3AF',
                fontSize: 12,
                marginTop: 4
              }}>
                {moment(note.date).format('MMM DD, YYYY - h:mm A')}
              </Text>
            </View>
          ))
        )}
      </ScrollView>
    </Card>
  );
};

const TasksSection = ({ tasks = [], onAddTask, onCompleteTask, theme }) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', dueDate: '', priority: 'medium' });

  const pendingTasks = tasks.filter(task => !task.completed);
  const completedTasks = tasks.filter(task => task.completed);

  const handleSubmitTask = () => {
    if (newTask.title.trim()) {
      onAddTask({
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        dueDate: newTask.dueDate || null,
        priority: newTask.priority
      });
      setNewTask({ title: '', description: '', dueDate: '', priority: 'medium' });
      setShowAddTask(false);
    }
  };

  const handleCancelTask = () => {
    setNewTask({ title: '', description: '', dueDate: '', priority: 'medium' });
    setShowAddTask(false);
  };

  return (
    <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$3" marginVertical="$2">
      <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
        <H4>Tasks ({pendingTasks.length} pending)</H4>
        <Pressable
          onPress={() => setShowAddTask(!showAddTask)}
          style={{
            backgroundColor: 'rgb(90,192,234)',
            borderRadius: 20,
            padding: 8
          }}
        >
          <AntDesign name="plus" size={16} color="white" />
        </Pressable>
      </XStack>

      {showAddTask && (
        <YStack gap="$2" marginBottom="$3" padding="$2" backgroundColor={theme ? '#F9FAFB' : '#374151'} borderRadius={8}>
          <Text style={{ fontWeight: '600', marginBottom: 8 }}>Add New Task</Text>

          {/* Task Title */}
          <TextInput
            style={{
              borderWidth: 1,
              borderColor: '#D1D5DB',
              borderRadius: 8,
              padding: 12,
              backgroundColor: theme ? '#fff' : '#374151',
              color: theme ? '#000' : '#fff'
            }}
            placeholder="Task title *"
            placeholderTextColor={theme ? '#9CA3AF' : '#6B7280'}
            value={newTask.title}
            onChangeText={(text) => setNewTask(prev => ({ ...prev, title: text }))}
          />

          {/* Task Description */}
          <TextInput
            style={{
              borderWidth: 1,
              borderColor: '#D1D5DB',
              borderRadius: 8,
              padding: 12,
              minHeight: 60,
              textAlignVertical: 'top',
              backgroundColor: theme ? '#fff' : '#374151',
              color: theme ? '#000' : '#fff'
            }}
            placeholder="Task description (optional)"
            placeholderTextColor={theme ? '#9CA3AF' : '#6B7280'}
            multiline
            value={newTask.description}
            onChangeText={(text) => setNewTask(prev => ({ ...prev, description: text }))}
          />

          {/* Priority and Due Date Row */}
          <XStack gap="$2">
            {/* Priority Dropdown */}
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, color: theme ? '#6B7280' : '#9CA3AF', marginBottom: 4 }}>Priority</Text>
              <Pressable
                style={{
                  borderWidth: 1,
                  borderColor: '#D1D5DB',
                  borderRadius: 8,
                  padding: 12,
                  backgroundColor: theme ? '#fff' : '#374151',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onPress={() => {
                  // Simple priority toggle
                  const priorities = ['low', 'medium', 'high'];
                  const currentIndex = priorities.indexOf(newTask.priority);
                  const nextIndex = (currentIndex + 1) % priorities.length;
                  setNewTask(prev => ({ ...prev, priority: priorities[nextIndex] }));
                }}
              >
                <Text style={{ color: theme ? '#000' : '#fff', textTransform: 'capitalize' }}>
                  {newTask.priority}
                </Text>
                <AntDesign name="down" size={12} color={theme ? '#6B7280' : '#9CA3AF'} />
              </Pressable>
            </View>

            {/* Due Date */}
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12, color: theme ? '#6B7280' : '#9CA3AF', marginBottom: 4 }}>Due Date</Text>
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: '#D1D5DB',
                  borderRadius: 8,
                  padding: 12,
                  backgroundColor: theme ? '#fff' : '#374151',
                  color: theme ? '#000' : '#fff'
                }}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={theme ? '#9CA3AF' : '#6B7280'}
                value={newTask.dueDate}
                onChangeText={(text) => setNewTask(prev => ({ ...prev, dueDate: text }))}
              />
            </View>
          </XStack>

          {/* Action Buttons */}
          <XStack gap="$2" marginTop="$2">
            <Pressable
              onPress={handleSubmitTask}
              style={{
                backgroundColor: 'rgb(90,192,234)',
                borderRadius: 6,
                padding: 12,
                flex: 1,
                alignItems: 'center'
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>
                Add Task
              </Text>
            </Pressable>
            <Pressable
              onPress={handleCancelTask}
              style={{
                backgroundColor: '#6B7280',
                borderRadius: 6,
                padding: 12,
                flex: 1,
                alignItems: 'center'
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>
                Cancel
              </Text>
            </Pressable>
          </XStack>
        </YStack>
      )}

      <ScrollView style={{ maxHeight: 300 }}>
        {pendingTasks.map((task, index) => (
          <XStack key={index} alignItems="center" gap="$3" padding="$2" marginBottom="$2" backgroundColor={theme ? '#F9FAFB' : '#4B5563'} borderRadius={8}>
            <Pressable onPress={() => onCompleteTask(task.id, !task.completed)}>
              <View style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                borderWidth: 2,
                borderColor: 'rgb(90,192,234)',
                backgroundColor: task.completed ? 'rgb(90,192,234)' : 'transparent',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {task.completed && <AntDesign name="check" size={12} color="white" />}
              </View>
            </Pressable>
            <YStack flex={1}>
              <SizableText size="$3" style={{ textDecorationLine: task.completed ? 'line-through' : 'none' }}>
                {task.title}
              </SizableText>
              {task.dueDate && (
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'}>
                  Due: {moment(task.dueDate).format('MMM DD')}
                </SizableText>
              )}
            </YStack>
            <View style={{
              backgroundColor: task.priority === 'high' ? '#EF4444' : task.priority === 'medium' ? '#F59E0B' : '#10B981',
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 4
            }}>
              <Text style={{ color: 'white', fontSize: 10 }}>{task.priority.toUpperCase()}</Text>
            </View>
          </XStack>
        ))}

        {/* Completed Tasks Section */}
        {completedTasks.length > 0 && (
          <>
            <Separator marginVertical="$3" />
            <H5 color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$2">
              Completed ({completedTasks.length})
            </H5>
            {completedTasks.map((task, index) => (
              <XStack key={`completed-${index}`} alignItems="center" gap="$3" padding="$2" marginBottom="$2" backgroundColor={theme ? '#F3F4F6' : '#4B5563'} borderRadius={8} opacity={0.7}>
                <Pressable onPress={() => onCompleteTask(task.id, !task.completed)}>
                  <View style={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    borderWidth: 2,
                    borderColor: 'rgb(90,192,234)',
                    backgroundColor: task.completed ? 'rgb(90,192,234)' : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {task.completed && <AntDesign name="check" size={12} color="white" />}
                  </View>
                </Pressable>
                <YStack flex={1}>
                  <SizableText size="$3" style={{ textDecorationLine: task.completed ? 'line-through' : 'none' }}>
                    {task.title}
                  </SizableText>
                  {task.dueDate && (
                    <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'}>
                      Due: {moment(task.dueDate).format('MMM DD')}
                    </SizableText>
                  )}
                  {task.completedAt && (
                    <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'}>
                      Completed: {moment(task.completedAt).format('MMM DD')}
                    </SizableText>
                  )}
                </YStack>
                <View style={{
                  backgroundColor: task.priority === 'high' ? '#EF4444' : task.priority === 'medium' ? '#F59E0B' : '#10B981',
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  borderRadius: 4
                }}>
                  <Text style={{ color: 'white', fontSize: 10 }}>{task.priority.toUpperCase()}</Text>
                </View>
              </XStack>
            ))}
          </>
        )}
      </ScrollView>
    </Card>
  );
};


const stages = [
  { title: "Select a product", description: "Select the product you want to make targeted content for" },
  { title: "Select a template", description: "Select the template you wish to apply the content in" },
  { title: "Preview", description: "Preview!" },
];

const TimeLineScreen = ({ route, navigation }) => {
  const { dataId } = route.params;
  const bottomSheetModalRef = useRef<BottomSheetModal>(null);
  const { data: itemData, schema: itemSchema, requestId } = route.params?.params;
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [flip, setFlip] = useState(false);
  const [owner, setOwner] = useState(null);
  const mapRef = useRef<MapView>(null);
  const [imageMode, setImageMode] = useState(false);
  const [data, setData] = useState(itemData);
  const [schema, setSchema] = useState(itemSchema);
  const theme = themeSelector();
  const [availableWidth, setAvailableWidth] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const { mail } = useSelector((state: DefaultRootState) => state.promiseData.data.userData.authentications);
  const whatsappNumbers = useSelector((state: DefaultRootState) => state.UI.messageOptions?.whatsappNumbers || []);
  const [mailHistory, setMailHistory] = useState([{ date: new Date(), mail: [], phone: [], notes: [], calendar: [], whatsapp: [] }]);
  const [activeDate, setActiveDate] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const googleAuth = googleAuths();
  const [aiResponse, setAIResponse] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const dispatch = useDispatch();
  const previewModalRef = useRef<PreviewModalHandler>(null);
  const [selectedRange, setSelectedRange] = useState({ startDate: new Date(), endDate: new Date() });
  const [flagged, setFlagged] = useState(itemData?.flagged);
  const { showConfirm, showAlert } = useAlertDialog();
  
  // Enhanced CRM state
  // Timeline data state
  const [timelineData, setTimelineData] = useState(null);
  const [contactNotes, setContactNotes] = useState([]);
  const [contactTasks, setContactTasks] = useState([]);
  const [timelineActivities, setTimelineActivities] = useState([]);
  const [contactMetrics, setContactMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('timeline'); // timeline, details, notes, tasks, metrics

  // Customer health analysis state
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthAnalyzed, setHealthAnalyzed] = useState(false);
  const [cacheMetadata, setCacheMetadata] = useState(null);

  // Fetch tracking refs to prevent duplicate calls
  const emailHistoryFetchedRef = useRef(false);
  const whatsappHistoryFetchedRef = useRef(false);
  const timelineDataFetchedRef = useRef(false);
  const healthDataFetchedRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);

  const handleCompleteTask = useCallback((taskId) => {
    setContactTasks(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    );
  }, []);

  const handleFlagToggle = useCallback(async () => {
    try {
      await flagItem(dataId, !flagged);
      setFlagged(!flagged);
      showAlert('Success', `Contact ${!flagged ? 'flagged' : 'unflagged'} successfully`);
    } catch (error) {
      showAlert('Error', 'Failed to update flag status');
    }
  }, [flagged, dataId]);

  const handleDelete = useCallback(async () => {
    const confirmDelete = await showConfirm(
      'Confirm item delete',
      'Are you sure you want to delete this item?',
      {
        cancelText: 'Cancel',
        confirmText: 'Delete'
      }
    )

    if (confirmDelete) {
      await handleDataItemDelete({ ids: [dataId] }).then(() => {
        navigation.goBack();
      })
    }
  }, [])

  // Optimized useFocusEffect with dependency tracking
  useFocusEffect(
    useCallback(() => {
      if (mail && !emailHistoryFetchedRef.current) {
        getEmailCount();
      }
    }, [mail])
  );

  // Phone number normalization helper
  const normalizePhoneNumber = useCallback((phone: string): string => {
    if (!phone) return '';
    // Remove spaces and + prefix for consistent matching
    return phone.replace(/\s+/g, '').replace(/^\+/, '');
  }, []);

  // Check if two phone numbers match (after normalization)
  const phoneNumbersMatch = useCallback((phone1: string, phone2: string): boolean => {
    const normalized1 = normalizePhoneNumber(phone1);
    const normalized2 = normalizePhoneNumber(phone2);
    return normalized1 && normalized2 && normalized1 === normalized2;
  }, [normalizePhoneNumber]);

  const _getEmailHistory = useCallback(async (mailAddress: string, forceRefresh = false) => {
    // Prevent duplicate calls unless forced
    if (emailHistoryFetchedRef.current && !forceRefresh) {
      console.log('📧 Email history already fetched, skipping...');
      return;
    }

    try {
      console.log('📧 Fetching email history for:', mailAddress);
      // Check if mail address is valid and exists before calling the API
      let res: any[] = []

      if (!mailAddress || !checkValidEmail(mailAddress)) {
        emailHistoryFetchedRef.current = true;
        return;
      } else {
        await apis.searchMail(googleAuth?.accessToken, mailAddress, {
          startDate: moment().startOf('year').unix(),
          endDate: moment().endOf('year').unix()
        }).then((response) => {
          res = response || [];
        });
      }

      const trackedItemHistory = await apis.getTrackedItemHistory(dataId);

      if (res || trackedItemHistory) {
        // Group data by date
        const groupedArray = (res || []).reduce((acc: any, item: any) => {
          const date = Number(item.internalDate);
          const day = moment(date).format('YYYY-MM-DD');
          if (!acc[day]) {
            acc[day] = { date, mail: [], phone: [], notes: [], calendar: [], whatsapp: [] };
          }
          // Add mail item to the respective date
          acc[day].mail.push(item);
          return acc;
        }, {});

        // Add tracked history dynamically based on `type`
        trackedItemHistory.forEach((entry: any) => {
          const entryDate = moment(entry.timestamp).format('YYYY-MM-DD');
          if (!groupedArray[entryDate]) {
            groupedArray[entryDate] = { date: entry.timestamp, mail: [], phone: [], notes: [], calendar: [], whatsapp: [] };
          }

          // Assign history based on `type`
          switch (entry.type) {
            case 'call':
              groupedArray[entryDate].phone.push(entry);
              break;
            case 'note':
              groupedArray[entryDate].notes.push(entry);
              break;
            case 'calendar':
              groupedArray[entryDate].calendar.push(entry);
              break;
            default:
              console.warn(`Unknown entry type: ${entry.type}`, entry);
          }
        });

        // Convert grouped object into array
        const groupedArrayValues = Object.values(
          Object.entries(groupedArray).map(([date, value]: [string, any]) => ({ ...value }))
        );

        // Ensure today is included in the array if not already
        if (!groupedArrayValues.some((item: any) => moment(item.date).isSame(moment(), 'day'))) {
          console.log('did not find today in the array')
          groupedArrayValues.unshift({ date: new Date(), mail: [], phone: [], notes: [], calendar: [], whatsapp: [] });
        }

        console.log('Grouped Array Values:', JSON.stringify(groupedArrayValues, null, 2));

        setMailHistory(groupedArrayValues);
        emailHistoryFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
      emailHistoryFetchedRef.current = true; // Mark as fetched even on error to prevent infinite loops
    }
  }, [googleAuth?.accessToken, dataId]);

  // Fetch WhatsApp history for the contact
  const _getWhatsAppHistory = useCallback(async (contactPhone: string, forceRefresh = false) => {
    // Prevent duplicate calls unless forced
    if (whatsappHistoryFetchedRef.current && !forceRefresh) {
      console.log('📱 WhatsApp history already fetched, skipping...');
      return;
    }

    // Check if WhatsApp integration is active
    if (!whatsappNumbers || whatsappNumbers.length === 0) {
      console.log('📱 No WhatsApp integration active, skipping WhatsApp history fetch');
      whatsappHistoryFetchedRef.current = true;
      return;
    }

    // Check if contact has a phone number
    if (!contactPhone) {
      console.log('📱 No contact phone number available, skipping WhatsApp history fetch');
      whatsappHistoryFetchedRef.current = true;
      return;
    }

    try {
      console.log('📱 Fetching WhatsApp history for contact phone:', contactPhone);
      const normalizedContactPhone = normalizePhoneNumber(contactPhone);
      console.log('📱 Normalized contact phone:', normalizedContactPhone);

      const allWhatsAppMessages: any[] = [];

      // Fetch conversations from all WhatsApp numbers
      for (const businessPhoneNumberId of whatsappNumbers) {
        try {
          console.log('📱 Fetching conversations for business phone number:', businessPhoneNumberId);
          const conversationsResponse = await getConversations(businessPhoneNumberId, { 
            page: 1, 
            limit: 100 
          });

          // Handle different response structures
          let conversations: any[] = [];
          if (Array.isArray(conversationsResponse)) {
            conversations = conversationsResponse;
          } else if (conversationsResponse?.conversations && Array.isArray(conversationsResponse.conversations)) {
            conversations = conversationsResponse.conversations;
          } else if (conversationsResponse?.data?.conversations && Array.isArray(conversationsResponse.data.conversations)) {
            conversations = conversationsResponse.data.conversations;
          }

          console.log('📱 Found conversations:', conversations.length);

          // Find conversations where participant phone matches contact phone
          for (const conversation of conversations) {
            const participantPhone = conversation.participantPhone || 
                                     conversation.participants?.find((p: string) => {
                                       const normalizedParticipant = normalizePhoneNumber(p);
                                       const normalizedBusinessId = normalizePhoneNumber(businessPhoneNumberId);
                                       return normalizedParticipant && normalizedParticipant !== normalizedBusinessId;
                                     }) ||
                                     conversation.participants?.[1] ||
                                     conversation.participants?.[0];

            if (participantPhone && phoneNumbersMatch(participantPhone, contactPhone)) {
              console.log('📱 Found matching conversation:', conversation.conversationId || conversation._id);
              
              // Fetch messages for this conversation
              try {
                const conversationId = conversation.conversationId || conversation._id || conversation.id;
                const messagesResponse = await getConversationMessages(conversationId, { 
                  page: 1, 
                  limit: 100 
                });

                const messages = messagesResponse?.data?.messages || messagesResponse?.messages || [];
                console.log('📱 Found messages for conversation:', messages.length);

                // Add conversation metadata to each message
                const messagesWithMetadata = messages.map((msg: any) => ({
                  ...msg,
                  conversationId: conversationId,
                  businessPhoneNumberId: businessPhoneNumberId,
                  contactName: conversation.contactName || participantPhone,
                  contactInfo: conversation.contactInfo || {},
                  participantPhone: participantPhone
                }));

                allWhatsAppMessages.push(...messagesWithMetadata);
              } catch (error) {
                console.error('📱 Error fetching messages for conversation:', error);
                // Continue with other conversations even if one fails
              }
            }
          }
        } catch (error) {
          console.error('📱 Error fetching conversations for business phone number:', businessPhoneNumberId, error);
          // Continue with other WhatsApp numbers even if one fails
        }
      }

      if (allWhatsAppMessages.length > 0) {
        console.log('📱 Total WhatsApp messages found:', allWhatsAppMessages.length);
        
        // Group messages by date and merge with existing timeline
        setMailHistory(prevHistory => {
          const groupedArray: any = {};
          
          // Initialize with existing history
          prevHistory.forEach((item: any) => {
            const day = moment(item.date).format('YYYY-MM-DD');
            groupedArray[day] = {
              date: item.date,
              mail: item.mail || [],
              phone: item.phone || [],
              notes: item.notes || [],
              calendar: item.calendar || [],
              whatsapp: item.whatsapp || []
            };
          });

          // Add WhatsApp messages grouped by date
          allWhatsAppMessages.forEach((message: any) => {
            const messageDate = message.timestamp ? new Date(message.timestamp) : new Date();
            const day = moment(messageDate).format('YYYY-MM-DD');
            
            if (!groupedArray[day]) {
              groupedArray[day] = { 
                date: messageDate, 
                mail: [], 
                phone: [], 
                notes: [], 
                calendar: [], 
                whatsapp: [] 
              };
            }
            
            groupedArray[day].whatsapp.push(message);
          });

          // Convert grouped object into array
          const groupedArrayValues = Object.values(
            Object.entries(groupedArray).map(([date, value]: [string, any]) => ({ ...value }))
          );

          // Ensure today is included in the array if not already
          if (!groupedArrayValues.some((item: any) => moment(item.date).isSame(moment(), 'day'))) {
            groupedArrayValues.unshift({ date: new Date(), mail: [], phone: [], notes: [], calendar: [], whatsapp: [] });
          }

          console.log('📱 Updated timeline with WhatsApp messages');
          return groupedArrayValues;
        });

        whatsappHistoryFetchedRef.current = true;
      } else {
        console.log('📱 No WhatsApp messages found for this contact');
        whatsappHistoryFetchedRef.current = true;
      }
    } catch (error) {
      console.error('📱 Error fetching WhatsApp history:', error);
      whatsappHistoryFetchedRef.current = true; // Mark as fetched even on error to prevent infinite loops
    }
  }, [whatsappNumbers, normalizePhoneNumber, phoneNumbersMatch]);

  // Fetch timeline data from new API endpoints - Optimized with tracking
  const fetchTimelineData = useCallback(async (forceRefresh = false) => {
    if (!dataId) return;
    
    // Prevent duplicate calls unless forced
    if (timelineDataFetchedRef.current && !forceRefresh) {
      console.log('📊 Timeline data already fetched, skipping...');
      return;
    }

    setLoading(true);
    try {
      console.log('📊 Fetching timeline data for dataId:', dataId);
      const response = await apis.getTimelineData(dataId);
      if (response && response.success) {
        setTimelineData(response.data);
        setContactNotes(response.data.notes || []);
        setContactTasks(response.data.tasks || []);
        setTimelineActivities(response.data.timeline || []);
        setContactMetrics(response.data.metrics || {});
        timelineDataFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      timelineDataFetchedRef.current = true; // Mark as fetched even on error
    } finally {
      setLoading(false);
    }
  }, [dataId]);

  // Add contact note - Optimized with useCallback
  const handleAddNote = useCallback(async (noteText, author = 'Current User') => {
    if (!noteText.trim()) return;
    
    try {
      const response = await apis.addContactNote(dataId, {
        text: noteText,
        author
      });
      
      if (response && response.success) {
        // Add the new note to local state immediately
        setContactNotes(prev => [...prev, response.note]);
        showAlert('Success', 'Note added successfully');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showAlert('Error', 'Failed to add note');
    }
  }, [dataId, showAlert]);

  // Add contact task - Optimized with useCallback
  const handleAddTask = useCallback(async (taskData) => {
    try {
      const response = await apis.addContactTask(dataId, taskData);
      
      if (response && response.success) {
        // Add the new task to local state immediately
        setContactTasks(prev => [...prev, response.task]);
        showAlert('Success', 'Task added successfully');
      }
    } catch (error) {
      console.error('Error adding task:', error);
      showAlert('Error', 'Failed to add task');
    }
  }, [dataId, showAlert]);

  // Toggle task completion - Optimized with useCallback
  const handleToggleTask = useCallback(async (taskId, completed) => {
    try {
      const response = await apis.toggleTask(dataId, taskId, completed);
      
      if (response && response.success) {
        // Update the task in local state immediately
        setContactTasks(prev => 
          prev.map(task => 
            task.id === taskId ? { ...task, completed: response.task.completed } : task
          )
        );
        showAlert('Success', `Task marked as ${completed ? 'completed' : 'incomplete'}`);
      }
    } catch (error) {
      console.error('Error toggling task:', error);
      showAlert('Error', 'Failed to update task');
    }
  }, [dataId, showAlert]);

  // Log interaction (email, phone, meeting) - Optimized with useCallback
  const handleLogInteraction = useCallback(async (type, details, duration = null, notes = '') => {
    try {
      const response = await apis.logInteraction(dataId, {
        type,
        details,
        duration,
        notes
      });
      
      if (response && response.success) {
        await fetchTimelineData(true); // Force refresh timeline
        showAlert('Success', 'Interaction logged successfully');
      }
    } catch (error) {
      console.error('Error logging interaction:', error);
      showAlert('Error', 'Failed to log interaction');
    }
  }, [dataId, fetchTimelineData, showAlert]);

  // Update contact metrics - Optimized with useCallback
  const handleUpdateMetrics = useCallback(async (metrics) => {
    try {
      const response = await apis.updateContactMetrics(dataId, metrics);
      
      if (response && response.success) {
        setContactMetrics(prev => ({ ...prev, ...response.metrics }));
        await fetchTimelineData(true); // Force refresh timeline
      }
    } catch (error) {
      console.error('Error updating metrics:', error);
      showAlert('Error', 'Failed to update metrics');
    }
  }, [dataId, fetchTimelineData, showAlert]);

  // Load cached customer health analysis - Optimized with useCallback and tracking
  const loadCachedHealthAnalysis = useCallback(async (forceRefresh = false) => {
    if (!dataId) {
      console.log('❌ No dataId available for health analysis');
      return;
    }

    // Prevent duplicate calls unless forced
    if (healthDataFetchedRef.current && !forceRefresh) {
      console.log('🏥 Health data already fetched, skipping...');
      return;
    }

    console.log('🔄 Loading cached health analysis for dataId:', dataId);
    try {
      const response = await apis.getCachedCustomerHealthAnalysis(dataId);
      console.log('📥 Cached health analysis response:', response);

      if (response && response.success) {
        console.log('✅ Cached health analysis loaded, setting health data:', response.data);

        // Map backend response to frontend interface
        const mappedData = {
          overallScore: response.data.health_score,
          scoreBreakdown: {
            engagement: response.data.score_breakdown.engagement,
            activity: response.data.score_breakdown.activity,
            value: response.data.score_breakdown.value,
            communication: response.data.score_breakdown.communication
          },
          metrics: {
            timeSpentOnWebsite: response.data.metrics.time_spent_website,
            numberOfEmails: response.data.metrics.email_count,
            totalMessages: response.data.metrics.total_messages,
            formsCompleted: response.data.metrics.forms_completed,
            lastContactDate: response.data.metrics.last_contact_date,
            averageResponseTime: response.data.metrics.avg_response_time
          },
          insights: response.data.insights,
          recommendations: response.data.recommendations,
          riskFactors: response.data.risk_factors,
          opportunities: response.data.opportunities
        };

        console.log('🗺️ Mapped health data:', mappedData);
        setHealthData(mappedData);
        setHealthAnalyzed(true);
        setCacheMetadata(response.metadata);
        healthDataFetchedRef.current = true;
        console.log('✅ Health data state updated');
      } else {
        console.log('❌ No successful response from cached health analysis:', response);
        healthDataFetchedRef.current = true;
      }
    } catch (error) {
      console.log('ℹ️ No cached health analysis found or error loading:', error.message);
      healthDataFetchedRef.current = true;
      // This is expected if no cached data exists
    }
  }, [dataId]);

  // Analyze customer health - Optimized with useCallback
  const analyzeCustomerHealth = useCallback(async () => {
    if (!dataId) {
      Alert.alert('Error', 'Customer ID not found');
      return;
    }

    console.log('🚀 Starting customer health analysis with dataId:', dataId);
    setHealthLoading(true);

    try {
      console.log('📡 Making API call to analyzeCustomerHealth...');
      const response = await apis.analyzeCustomerHealth(dataId);
      console.log('📊 Analysis response:', response);

      if (response && response.success) {
        console.log('✅ Analysis completed, mapping data:', response.data);

        // Map backend response to frontend interface
        const mappedData = {
          overallScore: response.data.health_score,
          scoreBreakdown: {
            engagement: response.data.score_breakdown.engagement,
            activity: response.data.score_breakdown.activity,
            value: response.data.score_breakdown.value,
            communication: response.data.score_breakdown.communication
          },
          metrics: {
            timeSpentOnWebsite: response.data.metrics.time_spent_website,
            numberOfEmails: response.data.metrics.email_count,
            totalMessages: response.data.metrics.total_messages,
            formsCompleted: response.data.metrics.forms_completed,
            lastContactDate: response.data.metrics.last_contact_date,
            averageResponseTime: response.data.metrics.avg_response_time
          },
          insights: response.data.insights,
          recommendations: response.data.recommendations,
          riskFactors: response.data.risk_factors,
          opportunities: response.data.opportunities
        };

        setHealthData(mappedData);
        setHealthAnalyzed(true);
        setCacheMetadata(null); // Clear cache metadata since this is fresh data

        showAlert('Success', 'Customer health analysis completed successfully');

        // Reload cached data to get updated metadata (force refresh)
        healthDataFetchedRef.current = false; // Reset flag to allow reload
        await loadCachedHealthAnalysis(true);
      } else {
        Alert.alert('Error', 'Failed to analyze customer health');
      }
    } catch (error) {
      console.error('💥 Error analyzing customer health:', error);
      Alert.alert('Error', `Failed to analyze customer health: ${error.message}`);
    } finally {
      setHealthLoading(false);
    }
  }, [dataId, loadCachedHealthAnalysis, showAlert]);



  const setActiveItem = useCallback((item) => {
    if (!item || activeDate === item) {
      setActiveDate(null);
    }
    else {
      setActiveDate(item);
    }
  }, [activeDate])

  // Optimize updateItem with useCallback
  const updateItem = useCallback(async () => {
    try {
      const res = await apis.getDataItemById(dataId);
      setData(res);
      setSchema(res.dataId.dataSchema);

      // Only fetch email history if we have a valid email and haven't fetched yet
      if (res?.data?.email && checkValidEmail(res.data.email) && !emailHistoryFetchedRef.current) {
        await _getEmailHistory(res.data.email);
      }
    } catch (error) {
      console.error('Error updating item:', error);
    }
  }, [dataId, _getEmailHistory]);

  // Optimize getUser with useCallback
  const getUser = useCallback(async () => {
    if (data?.posted_by) {
      const userId = typeof data.posted_by === 'string' ? data.posted_by : data.posted_by._id || data.posted_by.id;
      if (userId) {
        try {
          const res = await apis.getUserById(userId);
          setOwner(res);
        } catch (error) {
          console.error('Error fetching user:', error);
        }
      }
    }
  }, [data?.posted_by]);

  // Consolidated initialization effect - runs once on mount or when dataId changes
  useEffect(() => {
    if (initialLoadCompleteRef.current) {
      console.log('⚡ Initial load already complete, skipping...');
      return;
    }

    console.log('🚀 Initial load starting with dataId:', dataId);

    const initializeData = async () => {
      // Update item data if not provided
      if (!itemData || !itemSchema) {
        await updateItem();
      }

  // Fetch user info
      if (data) {
        await getUser();
      }

      // Fetch timeline data
      if (dataId) {
        await fetchTimelineData();
        await loadCachedHealthAnalysis();
      }

      // Fetch WhatsApp history if contact has phone number and WhatsApp is integrated
      if (data?.data?.phone || data?.data?.number || data?.data?.telephone) {
        const contactPhone = data?.data?.phone || data?.data?.number || data?.data?.telephone;
        await _getWhatsAppHistory(contactPhone);
      }

      initialLoadCompleteRef.current = true;
      console.log('✅ Initial load complete');
    };

    initializeData();
  }, [dataId, data, _getWhatsAppHistory]); // Include data and _getWhatsAppHistory in dependencies

  // Separate effect for email history when mail auth status changes
  useEffect(() => {
    // Only fetch if mail is authenticated, we have data, and haven't fetched yet
    if (mail?.mailAuthenticated && data?.data?.email && !emailHistoryFetchedRef.current) {
      console.log('📧 Mail authenticated, fetching email history...');
      _getEmailHistory(data.data.email);
    }
  }, [mail?.mailAuthenticated, data?.data?.email, _getEmailHistory]); // Include _getEmailHistory in dependencies

  // Separate effect for WhatsApp history when WhatsApp numbers or contact phone changes
  useEffect(() => {
    // Only fetch if WhatsApp is integrated, we have data, and haven't fetched yet
    if (whatsappNumbers && whatsappNumbers.length > 0 && 
        (data?.data?.phone || data?.data?.number || data?.data?.telephone) && 
        !whatsappHistoryFetchedRef.current) {
      const contactPhone = data?.data?.phone || data?.data?.number || data?.data?.telephone;
      console.log('📱 WhatsApp integrated, fetching WhatsApp history...');
      _getWhatsAppHistory(contactPhone);
    }
  }, [whatsappNumbers, data?.data?.phone, data?.data?.number, data?.data?.telephone, _getWhatsAppHistory]);

  const handleLayout = (e) => {
    setLayoutWidth(e.nativeEvent.layout.width);
  };

  const _checkPhoneNumber = useCallback(() => {
    if (data.phone && data.phone.length > 0) {
      Linking.openURL(`tel:${data.phone || data.number}`)
    } else {
      alert('No phone number stored under this contact')
    }
  }, [data]);

  const _checkEmail = useCallback(() => {
    if (data.data?.email && data.data?.email.length > 0) {
      Linking.openURL(`mailto:${data.data?.email}`)
    } else {
      alert('No email stored under this contact')
    }
  }, [data]);


  let center = {
    latitude: 51.496600,
    longitude: -0.381150,
    latitudeDelta: 0.0922,
    longitudeDelta: 0.0421,
  }

  const circleOptions = {
    strokeColor: '#5ac0eab3',
    strokeOpacity: 0.8,
    strokeWeight: 50,
    fillColor: '#5ac0ea',
    fillOpacity: 0.35,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: 400,
    zIndex: 1
  }

  const showEmailPreview = () => {
    previewModalRef.current?.openModal(activeTemplate);
  }

  const handleShare = async () => {
    const url = Linking.createURL(`/form/${data._id}`, { queryParams: { dataTitle: data.dataTitle } })
    try {
      const result = await Share.share({
        message: `Check out this post on SmartSaaS: ${data.dataTitle} by ${owner?.profile_information.name} ${owner?.profile_information.surname}!`,
        url: url,
        title: 'SmartSaaS'
      }, {
        dialogTitle: 'Share this post',
        tintColor: 'green'
      })
      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error) {
      alert(error.message)
    }
  }

  const _handleUpdate = useCallback(async (values) => {
    try {
      const res = await apis.updateDataItem(dataId, values);
      if (res) {
        setData(res);
        alert('Data updated successfully');
        // Force refresh email history if email was updated
        if (values.email && values.email !== data?.data?.email) {
          emailHistoryFetchedRef.current = false;
          await _getEmailHistory(values.email, true);
        }
      }
    } catch (error) {
      console.error('Error updating data:', error);
      alert('Failed to update data');
    }
  }, [dataId, data?.data?.email, _getEmailHistory]);

  const _handleEditOpen = useCallback(() => {
    if (activeDate && !editOpen) {
      setActiveDate(null);
    }
    setEditOpen(!editOpen);
  }, [activeDate, editOpen]);

  const showAIPreviewer = useCallback(() => {
    bottomSheetModalRef.current?.present();
  }, []);

  const requestAI = useCallback(async () => {
    setFetching(true);
    try {
      const response = await fetch("http://localhost:11435/enrich-data", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: selectedProduct?.data?.product_name || "Default Product",
          product_description: selectedProduct?.data?.product_description || "A health device that helps you monitor your health",
          business_name: "Cake Bake and Sweets Show",
          business_type: "Events Industry"
        })
      });
      
      if (!response.ok) {
        console.log('Error fetching AI data');
        return;
      }

      const data = await response.json();
      setAIResponse(data);
    } catch (error) {
      console.error('Error fetching AI data:', error);
    } finally {
      setFetching(false);
    }
  }, [selectedProduct]);

  return (
    <DefaultScreen 
      scrollDisabled 
      backButtonStyle={{ top: 20 }} 
      backButton 
      removeMargin 
      containerStyle={{ top: Platform.select({ web: -20, default: undefined }) }} 
      searchBarStyle={{ minWidth: "105%" }} 
      style={{ paddingTop: Platform.select({ default: 10, web: undefined }), paddingBottom: 50 }}
      searchBar={{
        showSearch: false,
        customStyle: { left: 20 },
        otherComponents: [
          { function: _checkEmail, functionName: 'sendEmail', icon: <AntDesign name="mail" size={24} color={theme ? "black" : "white"} /> },
          { function: _checkPhoneNumber, functionName: 'callUser', icon: <AntDesign name="phone" size={24} color={theme ? "black" : "white"} /> },
          { function: () => setActiveView('notes'), functionName: 'notes', icon: <Foundation name="clipboard-notes" size={24} color={theme ? "black" : "white"} style={{ left: 5 }} /> },
          { function: () => setEditOpen(!editOpen), functionName: 'edit', icon: <AntDesign name="edit" size={24} color={theme ? "black" : "white"} /> },
          { function: () => setActiveView('tasks'), functionName: 'tasks', icon: <AntDesign name="calendar" size={24} color={theme ? "black" : "white"} /> },
          { function: handleShare, functionName: 'share', icon: <AntDesign name="share-alt" size={24} color={theme ? "black" : "white"} /> },
          { function: () => { setFlip(!flip); setImageMode(true) }, functionName: 'picture', icon: <AntDesign name="picture" size={24} color={theme ? "black" : "white"} style={{ backgroundColor: flip ? 'rgb(90,192,234)' : undefined, borderRadius: 10, overflow: 'hidden' }} /> },
          {
            function: () => showAIPreviewer(), 
            functionName: 'ai', 
            icon: <Pressable onPress={() => showAIPreviewer()} style={{ flex: 1, maxHeight: 30, maxWidth: 30 }}>
              <LottieView source={require("@assets/animations/automation_robot.json")} style={{ flex: 1 }} />
            </Pressable>
          },
        ],
        extraComponents: Platform.select({
          web: (
            <View style={{ maxWidth: 300, top: -15, alignSelf: "flex-end" }}>
              <RangePicker
                startDate={new Date()}
                endDate={new Date()}
                dateCallback={(date) => {
                  setSelectedRange({
                    startDate: date.startDate,
                    endDate: date.endDate,
                  });
                }}
              />
            </View>
          ),
          default: undefined
        })
      }}
    >
      <PreviewModal ref={previewModalRef} />
      
      <XStack flex={1}>
        {/* Detail Card Section */}
        <Card flexBasis={400} backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" margin="$2" borderRadius={16}>
          <XStack alignItems="center" gap="$4">
            {/* Contact Avatar */}
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: theme ? '#E5E7EB' : '#374151',
              overflow: 'hidden',
              borderWidth: 3,
              borderColor: 'rgb(90,192,234)'
            }}>
              {data?.image ? (
                <Image 
                  contentFit='cover' 
                  source={{ uri: data.image }} 
                  style={{ width: '100%', height: '100%' }} 
                />
              ) : (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <UserIcon height={60} width={60} />
                </View>
              )}
            </View>

            {/* Contact Info */}
            <YStack flex={1} gap="$2">
              <XStack alignItems="center" gap="$2">
                <H2 color={theme ? '#1F2937' : '#F9FAFB'}>
                  {data?.data?.name} {data?.data?.surname || ""}
                </H2>
                <ContactStatusBadge status={data?.data?.status || 'Active'} theme={theme} />
                {flagged && <AntDesign name="flag" size={16} color="#EF4444" />}
              </XStack>
              
              <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'}>
                {data?.data?.email || 'No email provided'}
              </SizableText>
              
              <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'}>
                {data?.data?.company || data?.data?.organization || 'No company'}
              </SizableText>
              
              <XStack gap="$2" alignItems="center">
                <SizableText size="$2" color={theme ? '#9CA3AF' : '#6B7280'}>
                  Last contact: {moment(data?.updatedAt).fromNow()}
                </SizableText>
              </XStack>
            </YStack>

            {/* Quick Actions */}
            <YStack gap="$2">
              <ContactStatusBadge 
                status={data?.data?.leadScore ? `Score: ${data.data.leadScore}` : 'No Score'} 
                theme={theme} 
              />
            </YStack>
          </XStack>
        </Card>
        <YStack>
        {/* Navigation Tabs */}
        <Card backgroundColor={theme ? '#ffffff' : '#374151'} margin="$2" borderRadius={16}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: 8,
              paddingVertical: 8,
              gap: 4
            }}
          >
            <XStack gap="$1" alignItems="center">
              {[
                { key: 'timeline', label: 'Timeline', icon: 'clock-circle' },
                { key: 'details', label: 'Details', icon: 'user' },
                { key: 'notes', label: 'Notes', icon: 'file-text' },
                { key: 'tasks', label: 'Tasks', icon: 'check-circle' },
                { key: 'metrics', label: 'Metrics', icon: 'bar-chart' }
              ].map((tab) => (
                <Pressable
                  key={tab.key}
                  onPress={() => setActiveView(tab.key)}
                  style={{
                    minWidth: 80,
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    borderRadius: 12,
                    backgroundColor: activeView === tab.key ? 'rgb(90,192,234)' : 'transparent',
                    alignItems: 'center'
                  }}
                >
                  <AntDesign
                    name={tab.icon as any} 
                    size={Platform.select({ default: 18, web: 20 })}
                    color={activeView === tab.key ? 'white' : (theme ? '#6B7280' : '#9CA3AF')}
                  />
                  <SizableText 
                    size={Platform.select({ default: "$1", web: "$2" })}
                    color={activeView === tab.key ? 'white' : (theme ? '#6B7280' : '#9CA3AF')}
                    marginTop="$1"
                  >
                    {tab.label}
                  </SizableText>
                </Pressable>
              ))}
            </XStack>
          </ScrollView>
        </Card>

        {/* Content Area */}
        <YStack flex={1} paddingHorizontal="$2">
          {activeView === 'timeline' && (
            <YStack flex={1}>
              {Platform.select({
                default: (
                  <View style={{
                    marginBottom: 12,
                    alignItems: 'center',
                    minHeight: 70,
                    transform: [{ scale: 0.8 }]
                  }}>
                    <RangePicker
                      startDate={selectedRange.startDate}
                      endDate={selectedRange.endDate}
                      dateCallback={(date) => {
                        setSelectedRange({
                          startDate: date.startDate,
                          endDate: date.endDate,
                        });
                      }}
                    />
                  </View>
                ),
                web: null
              })}
              <Card backgroundColor={theme ? '#ffffff' : '#374151'} flex={1} padding="$3" borderRadius={16}>
                <H5 marginBottom="$3">Activity Timeline</H5>

                {activeDate ? (
                  <YStack flex={1}>
                    <XStack alignItems="center" gap="$2" marginBottom="$3">
                      <Pressable onPress={() => setActiveDate(null)}>
                        <AntDesign name="arrow-left" size={24} color="rgb(90,192,234)" />
                      </Pressable>
                      <H4>
                        {moment(activeDate.date).format('MMMM DD, YYYY')}
                      </H4>
                    </XStack>
                    <View style={{ flex: 1 }}>
                      <HistoryComponent history={activeDate} keyboardCallback={setKeyboardVisible} />
                    </View>
                  </YStack>
                ) : (
                  <YStack flex={1}>
                    {mailHistory.length > 0 ? (
                        Platform.select({
                          web: (
                            <TimeLineScroll
                              hideRange
                              initialRange={{
                                startDate: selectedRange.startDate.toISOString(),
                                endDate: selectedRange.endDate.toISOString()
                              }}
                              vertical
                              history={mailHistory}
                              callBack={(item) => setActiveItem(item)}
                              onSnapToItem={(value) => console.log('snapped timeline value', value)}
                            />
                          ),
                          default: (
                            <ScrollView style={{ flex: 1 }}>
                              {mailHistory
                                .filter((item: any) => {
                                  const itemDate = moment(item.date);
                                  return itemDate.isSameOrAfter(moment(selectedRange.startDate).startOf('day')) &&
                                    itemDate.isSameOrBefore(moment(selectedRange.endDate).endOf('day'));
                                })
                                .map((item: any, index: number) => (
                                  <Pressable
                                    key={index}
                                    onPress={() => setActiveItem(item)}
                                    style={{
                                      padding: 16,
                                      marginBottom: 12,
                                      backgroundColor: theme ? '#F9FAFB' : '#4B5563',
                                      borderRadius: 12,
                                      borderLeftWidth: 4,
                                      borderLeftColor: 'rgb(90,192,234)'
                                    }}
                                  >
                                    <XStack alignItems="center" justifyContent="space-between">
                                      <YStack flex={1}>
                                        <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                                          {moment(item.date).format('MMMM DD, YYYY')}
                                        </SizableText>
                                        <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginTop="$1">
                                          {item.mail?.length || 0} emails • {item.phone?.length || 0} calls • {item.notes?.length || 0} notes{item.whatsapp?.length ? ` • ${item.whatsapp.length} WhatsApp messages` : ''}
                                        </SizableText>
                                      </YStack>
                                      <AntDesign name="right" size={16} color={theme ? '#6B7280' : '#9CA3AF'} />
                                    </XStack>
                                  </Pressable>
                                ))}
                            </ScrollView>
                          )
                        })
                    ) : (
                      <View style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 40
                      }}>
                        <AntDesign name="clock-circle" size={48} color="#9CA3AF" />
                        <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} textAlign="center" marginTop="$3">
                          No timeline data available
                        </SizableText>
                        <SizableText size="$3" color={theme ? '#9CA3AF' : '#6B7280'} textAlign="center" marginTop="$2">
                          Timeline will appear here as you interact with this contact
                        </SizableText>
                      </View>
                    )}
                  </YStack>
                )}
              </Card>
            </YStack>
          )}

          {activeView === 'details' && (
            <YStack flex={1}>
              <Card backgroundColor={theme ? '#ffffff' : '#374151'} flex={1} padding="$3" borderRadius={16}>
                <H5 marginBottom="$3">Contact Details</H5>

                {!editOpen ? (
                  <ScrollView style={{ flex: 1 }}>
                    {/* Contact Information Display */}
                    <YStack gap="$3">
                      {Object.entries(data?.data || {}).map(([key, value]) => {
                        if (!value || key === 'id') return null;
                        return (
                          <YStack key={key} gap="$1">
                            <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} fontWeight="600">
                              {toTitleCase(key.replace(/_/g, ' '))}
                            </SizableText>
                            <SizableText size="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                              {String(value)}
                            </SizableText>
                          </YStack>
                        );
                      })}
                    </YStack>

                    <Pressable
                      onPress={() => setEditOpen(true)}
                      style={{
                        backgroundColor: 'rgb(90,192,234)',
                        borderRadius: 8,
                        padding: 12,
                        alignItems: 'center',
                        marginTop: 20
                      }}
                    >
                      <Text style={{ color: 'white', fontWeight: '600' }}>
                        Edit Contact
                      </Text>
                    </Pressable>
                  </ScrollView>
                ) : (
                  <ScrollView>
                    <GenerateSchemaForm
                      retainInitialValues
                      Progress={editOpen}
                      initialData={data?.data}
                      schema={schema}
                      submitFunction={_handleUpdate}
                      other={requestId}
                      avoidKeyboardDisabled
                      addOffset={190}
                      buttonLabel='Update Contact'
                      hasTitle
                      containerWidth={availableWidth}
                      noBackground
                    />
                    <Pressable
                      onPress={() => setEditOpen(false)}
                      style={{
                        backgroundColor: '#6B7280',
                        borderRadius: 8,
                        padding: 12,
                        alignItems: 'center',
                        marginTop: 10
                      }}
                    >
                      <Text style={{ color: 'white', fontWeight: '600' }}>
                        Cancel
                      </Text>
                    </Pressable>
                  </ScrollView>
                )}
              </Card>
            </YStack>
          )}

          {activeView === 'notes' && (
            <NotesSection 
              notes={contactNotes} 
              onAddNote={handleAddNote} 
              theme={theme} 
            />
          )}

          {activeView === 'tasks' && (
            <TasksSection 
              tasks={contactTasks} 
              onAddTask={handleAddTask} 
              onCompleteTask={handleToggleTask} 
              theme={theme} 
            />
          )}

          {activeView === 'metrics' && (
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 20 }}>
              <YStack gap="$3">
                <ContactMetrics data={contactMetrics} theme={theme} healthData={healthData} />

                {/* Health Analysis Section */}
                <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$3" marginVertical="$2">
                <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
                  <H4>Health Analysis</H4>
                  {!healthAnalyzed && (
                    <Pressable
                      onPress={analyzeCustomerHealth}
                      disabled={healthLoading}
                      style={{
                        backgroundColor: 'rgb(90,192,234)',
                        borderRadius: 8,
                        paddingHorizontal: 16,
                        paddingVertical: 8,
                        opacity: healthLoading ? 0.7 : 1
                      }}
                    >
                      <Text style={{ color: 'white', fontWeight: '600', fontSize: 12 }}>
                        {healthLoading ? 'Analyzing...' : 'Analyze Health'}
                      </Text>
                    </Pressable>
                  )}
                </XStack>

                {healthData ? (
                  <YStack gap="$3">
                    {/* Overall Score */}
                    <XStack justifyContent="space-between" alignItems="center" padding="$2" backgroundColor={theme ? '#F9FAFB' : '#4B5563'} borderRadius={8}>
                      <SizableText size="$3" fontWeight="600">Overall Health Score</SizableText>
                      <View style={{
                        backgroundColor: healthData.overallScore >= 80 ? '#10B981' : healthData.overallScore >= 60 ? '#F59E0B' : '#EF4444',
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 16,
                        minWidth: 60,
                        alignItems: 'center'
                      }}>
                        <Text style={{ color: 'white', fontWeight: '700', fontSize: 16 }}>
                          {healthData.overallScore}/100
                        </Text>
                      </View>
                    </XStack>

                    {/* Score Breakdown */}
                    <YStack gap="$2">
                      <SizableText size="$3" fontWeight="600" marginBottom="$2">Score Breakdown</SizableText>
                      {Object.entries(healthData.scoreBreakdown).map(([key, value]) => (
                        <XStack key={key} justifyContent="space-between" alignItems="center" padding="$2" backgroundColor={theme ? '#F9FAFB' : '#4B5563'} borderRadius={6}>
                          <SizableText size="$3" style={{ textTransform: 'capitalize' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </SizableText>
                          <SizableText size="$3" fontWeight="600">
                            {value}/100
                          </SizableText>
                        </XStack>
                      ))}
                    </YStack>

                    {/* Key Insights */}
                    {healthData.insights && healthData.insights.length > 0 && (
                      <YStack gap="$2">
                        <SizableText size="$3" fontWeight="600">Key Insights</SizableText>
                        {healthData.insights.slice(0, 3).map((insight, index) => (
                          <XStack key={index} alignItems="flex-start" gap="$2" padding="$2" backgroundColor={theme ? '#F9FAFB' : '#4B5563'} borderRadius={6}>
                            <Text style={{ fontSize: 12, marginTop: 2 }}>💡</Text>
                            <SizableText size="$2" flex={1}>
                              {insight}
                            </SizableText>
                          </XStack>
                        ))}
                      </YStack>
                    )}

                    {/* Risk Factors */}
                    {healthData.riskFactors && healthData.riskFactors.length > 0 && (
                      <YStack gap="$2">
                        <SizableText size="$3" fontWeight="600" color="#EF4444">Risk Factors</SizableText>
                        {healthData.riskFactors.slice(0, 2).map((risk, index) => (
                          <XStack key={index} alignItems="flex-start" gap="$2" padding="$2" backgroundColor="#FEF2F2" borderRadius={6}>
                            <Text style={{ fontSize: 12, marginTop: 2 }}>⚠️</Text>
                            <SizableText size="$2" flex={1} color="#EF4444">
                              {risk}
                            </SizableText>
                          </XStack>
                        ))}
                      </YStack>
                    )}

                    {/* Cache Info */}
                    {cacheMetadata && (
                      <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} textAlign="center" marginTop="$2">
                        Last analyzed: {new Date(cacheMetadata.cachedAt).toLocaleString()}
                        {cacheMetadata.isFresh && ' (Fresh)'}
                      </SizableText>
                    )}
                  </YStack>
                ) : (
                  <YStack alignItems="center" padding="$4">
                    <Text style={{ fontSize: 48, marginBottom: 16 }}>🏥</Text>
                    <SizableText size="$4" color={theme ? '#6B7280' : '#9CA3AF'} textAlign="center" marginBottom="$2">
                      No Health Analysis Available
                    </SizableText>
                    <SizableText size="$3" color={theme ? '#9CA3AF' : '#6B7280'} textAlign="center">
                      Click "Analyze Health" to get insights about this contact's engagement and health score.
                    </SizableText>
                  </YStack>
                )}
              </Card>
              </YStack>
            </ScrollView>
          )}
        </YStack>

        {/* Quick Action Bar */}
        <Card backgroundColor={theme ? '#ffffff' : '#374151'} margin="$2" borderRadius={16}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: Platform.select({ default: 8, web: 12 }),
              paddingVertical: Platform.select({ default: 8, web: 12 }),
              gap: Platform.select({ default: 8, web: 12 })
            }}
          >
            <XStack gap={Platform.select({ default: "$2", web: "$3" })} alignItems="center">
              <QuickActionCard
                icon={<AntDesign name="phone" size={Platform.select({ default: 16, web: 20 })} color={data?.data?.phone ? 'rgb(90,192,234)' : '#9CA3AF'} />}
                label="Call"
                onPress={() => {
                  try {
                    Linking.openURL(`tel:${data?.data?.phone || data?.data?.number || data?.data?.telephone}`)
                  } catch (e) {
                    showAlert('Error', `Unable to make a call: ${e}`)
                  }
                }}
                disabled={!data?.data?.phone && !data?.data?.number && !data?.data?.telephone}
                theme={theme}
              />

              <QuickActionCard
                icon={<AntDesign name="mail" size={Platform.select({ default: 16, web: 20 })} color={data?.data?.email ? 'rgb(90,192,234)' : '#9CA3AF'} />}
                label="Email"
                onPress={() => Linking.openURL(`mailto:${data?.data?.email}`)}
                disabled={!data?.data?.email}
                theme={theme}
              />

              <QuickActionCard
                icon={<AntDesign name="flag" size={Platform.select({ default: 16, web: 20 })} color={flagged ? '#EF4444' : '#9CA3AF'} />}
                label="Flag"
                onPress={handleFlagToggle}
                theme={theme}
              />

              <QuickActionCard
                icon={<LottieView source={require("@assets/animations/automation_robot.json")} style={{ height: Platform.select({ default: 16, web: 20 }), width: Platform.select({ default: 16, web: 20 }) }} />}
                label="AI"
                onPress={showAIPreviewer}
                theme={theme}
              />

              <QuickActionCard
                icon={<Text style={{ fontSize: Platform.select({ default: 14, web: 16 }) }}>🏥</Text>}
                label="Health"
                onPress={analyzeCustomerHealth}
                disabled={healthLoading}
                theme={theme}
              />

              <QuickActionCard
                icon={<AntDesign name="calendar" size={Platform.select({ default: 16, web: 20 })} color="rgb(90,192,234)" />}
                label="Schedule"
                onPress={() => console.log("Schedule meeting")}
                theme={theme}
              />

              <QuickActionCard
                icon={<AntDesign name="delete" size={Platform.select({ default: 16, web: 20 })} color="#EF4444" />}
                label="Delete"
                onPress={handleDelete}
                theme={theme}
              />
            </XStack>
          </ScrollView>
        </Card>
        </YStack>
      </XStack>
      {/* AI Preview Modal */}
      <BottomSheetModal
        ref={bottomSheetModalRef}
        snapPoints={['70%']}
        onChange={() => {
          dispatch({ type: HIDE_USER_CONTEXT_MODAL })
        }}
        style={{ borderRadius: 20, maxWidth: "80%", marginLeft: "10%", alignSelf: "center" }}
      >
        <BottomSheetView style={{ 
          paddingHorizontal: 30, 
          justifyContent: "center", 
          alignContent: "center", 
          flex: 1, 
          alignItems: "center", 
          backgroundColor: theme ? 'white' : '#1F2937', 
          paddingBottom: 30 
        }}>
          <XStack gap="$4" alignItems="center" marginBottom="$6">
            <LottieView 
              source={require("@assets/animations/automation_robot.json")} 
              style={{ height: 60, width: 60 }} 
            />
            <H2 color={theme ? '#1F2937' : '#F9FAFB'}>AI Assistant</H2>
          </XStack>

          <YStack flex={1} gap="$4" width="100%">
            <XStack gap="$3" height="100%">
              {activeStep >= 0 && (
                <Card flex={1} padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
                  <H4 textAlign="center" marginBottom="$3">Select Product</H4>
                  {selectedProduct && (
                    <YStack marginBottom="$3">
                      <SizableText fontWeight="600">{toTitleCase(selectedProduct.data?.product_name || 'Selected Product')}</SizableText>
                      <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'}>{selectedProduct.data?.product_description || "Product description"}</SizableText>
                    </YStack>
                  )}
                  <GenerateSchemaForm 
                    customStyling={{ flex: 1 }}
                    initialData={selectedProduct}
                    columns={1} 
                    schema={{ 
                      stage_action: { 
                        type: 'string', 
                        helpText: "Select a product for AI content generation", 
                        ref: "product_select" 
                      } 
                    }} 
                    submitFunction={(vals) => {
                      setActiveStep(1);
                      setSelectedProduct(vals.stage_action[0]);
                    }} 
                    other={requestId} 
                    avoidKeyboardDisabled 
                    hideButton={!!selectedProduct} 
                    addOffset={190} 
                    buttonLabel='Select Product' 
                    hasTitle 
                    containerWidth={availableWidth} 
                    noBackground 
                  />
                </Card>
              )}

              {activeStep >= 1 && (
                <Card flex={1} padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
                  <H4 textAlign="center" marginBottom="$3">Select Template</H4>
                  <GenerateSchemaForm 
                    customStyling={{ flex: 1 }}
                    columns={1} 
                    initialData={activeTemplate} 
                    schema={{ 
                      stage_action: { 
                        type: 'string', 
                        helpText: "Choose a template for content generation", 
                        ref: "template_select" 
                      } 
                    }} 
                    submitFunction={(vals) => {
                      setActiveTemplate(vals.stage_action[0]);
                      setActiveStep(2);
                    }} 
                    other={requestId} 
                    avoidKeyboardDisabled 
                    addOffset={190} 
                    buttonLabel='Select Template' 
                    hasTitle 
                    containerWidth={availableWidth} 
                    noBackground 
                  />
                </Card>
              )}

              {activeStep > 1 && (
                <Card flex={2} padding="$4" backgroundColor={theme ? '#ffffff' : '#374151'}>
                  <H4 textAlign="center" marginBottom="$3">AI Preview</H4>
                  {fetching ? (
                    <YStack flex={1} justifyContent="center" alignItems="center">
                      <LottieView 
                        source={require("@assets/animations/spin-leaf-loader.json")} 
                        autoPlay 
                        loop 
                        style={{ height: 100, width: 100 }} 
                      />
                      <SizableText textAlign="center" marginTop="$3">
                        Generating AI content...
                      </SizableText>
                    </YStack>
                  ) : (
                    <YStack flex={1} gap="$3">
                      {aiResponse ? (
                        <YStack flex={1}>
                          <Button 
                            customStyle={{ marginBottom: 15 }} 
                            buttonText="Preview Email" 
                            onPress={showEmailPreview} 
                          />
                          <ScrollView style={{ flex: 1 }}>
                            <Card padding="$3" backgroundColor={theme ? '#F9FAFB' : '#374151'}>
                              <Markdown style={{
                                body: { color: theme ? '#1F2937' : '#F9FAFB' },
                                heading1: { color: theme ? '#1F2937' : '#F9FAFB' },
                                heading2: { color: theme ? '#1F2937' : '#F9FAFB' }
                              }}>
                                {JSON.stringify(aiResponse, null, 2)}
                              </Markdown>
                            </Card>
                          </ScrollView>
                        </YStack>
                      ) : (
                        <YStack flex={1} justifyContent="center" alignItems="center">
                          <Button 
                            buttonText="Generate AI Content" 
                            onPress={requestAI} 
                            icon="eyeo" 
                            customStyle={{ minWidth: "80%", borderRadius: 10 }} 
                          />
                        </YStack>
                      )}
                    </YStack>
                  )}
                </Card>
              )}
            </XStack>

            {/* Progress Stepper */}
            <Card padding="$3" backgroundColor={theme ? '#F9FAFB' : '#374151'} marginTop="$4">
              <XStack justifyContent="space-between" alignItems="center">
                {stages.map((stage, index) => (
                  <React.Fragment key={index}>
                    <YStack alignItems="center" flex={1}>
                      <View style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: index <= activeStep ? 'rgb(90,192,234)' : '#D1D5DB',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 8
                      }}>
                        <Text style={{ 
                          color: index <= activeStep ? 'white' : '#6B7280', 
                          fontWeight: '600',
                          fontSize: 14
                        }}>
                          {index + 1}
                        </Text>
                      </View>
                      <SizableText 
                        size="$2" 
                        textAlign="center" 
                        color={index <= activeStep ? 'rgb(90,192,234)' : (theme ? '#9CA3AF' : '#6B7280')}
                        fontWeight={index <= activeStep ? '600' : '400'}
                      >
                        {stage.title}
                      </SizableText>
                    </YStack>
                    {index < stages.length - 1 && (
                      <View style={{
                        height: 2,
                        flex: 1,
                        backgroundColor: index < activeStep ? 'rgb(90,192,234)' : '#D1D5DB',
                        marginHorizontal: 8,
                        marginTop: -20
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </XStack>
            </Card>
          </YStack>
        </BottomSheetView>
      </BottomSheetModal>
    </DefaultScreen>
  );
};

export default TimeLineScreen;
